import "./SearchBar.scss";

import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import clsx from "clsx";
import Link from "../../nav/Link/Link";
import * as Utils from "../../../lib/util/Utils";
import * as AnalyticsUtils from "../../../lib/util/AnalyticsUtils";
import { sendEvent, TrackingContext } from "../../../lib/util/AnalyticsUtils";
import * as strings from "../../../strings";

import SearchBarNoResults from "./sub-components/SearchBarNoResults";
import SearchBarFetching from "./sub-components/SearchBarFetching";
import SearchBarResults from "./sub-components/SearchBarResults";
import SearchBarInput from "./sub-components/SearchBarInput";
import Tooltip from "../../common/Tooltip/Tooltip";
import { TextElement } from "../../../lib/util/LocaleUtils";
import AsyncState, { useAsyncState } from "react-async-stateful";
import { User } from "../../../lib/types/UserTypes";

export interface SearchBarProps<T> {
	isOpen: boolean;
	onOpenChange?: (open: boolean) => void;
	className?: string;
	placeholder?: TextElement;
	focusPlaceholder?: TextElement;
	onGetResultLink?: any;
	onFilterItem?: any;
	hideIcon?: boolean;
	onChange?: (value: string) => void;
	onEnterPress?: () => void;
	onSelect?: (item: T, e: React.SyntheticEvent) => void;
	autocomplete?: AsyncState<T[]>;
	onAutocomplete?: (input: string) => void;
	seeAll?: SearchSeeAllOptions;
}

export interface SearchSeeAllOptions {
	text?: TextElement;
	href: string;
}

const MAX_RESULTS = 6;

/* TODO: Known bug whereby a user keyboard (with up + down arrayow) selects result 5 and then changes
         their input so that there is only 3 results the selection will point
         to a result that does not exist*/
function SearchBar<T>(props: SearchBarProps<T>) {
	const {
		isOpen,
		onOpenChange,
		className,
		placeholder,
		focusPlaceholder,
		onGetResultLink,
		onFilterItem,
		onChange,
		onEnterPress,
		autocomplete,
		onSelect,
		hideIcon,
		seeAll,
		onAutocomplete,
	} = props;

	const trackingContext = useContext(TrackingContext);

	const ref = useRef<HTMLDivElement>(null);

	const [selection, setSelection] = useState(0);
	const [input, setInput] = useState("");

	useEffect(() => {
		if (!isOpen) {
			setInput("");
		}
	}, [isOpen]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleAutocomplete = useCallback(
		Utils.debounce((input: string) => {
			if (onAutocomplete) {
				onAutocomplete(input);
			}
		}, 500),
		[onAutocomplete],
	);

	const handleSearchInput = useCallback(
		(e: any) => {
			const newInput = e.target.value;

			if (!input) {
				sendEvent({
					eventCategory: "Search Bar",
					eventAction: "search",
					eventLabel:
						typeof trackingContext === "string"
							? trackingContext
							: trackingContext.ga,
					mixpanelProperties: {
						name: "Search Bar Input",
						Context: trackingContext,
					},
				});
			}

			setInput(newInput);
			if (onChange) onChange(newInput);
			handleAutocomplete(newInput);
		},
		[handleAutocomplete, input, onChange, trackingContext],
	);

	const handleReset = useCallback(() => {
		setSelection(0);
		setInput("");
	}, []);

	const handleSeeAllClick = useCallback(() => {
		if (onOpenChange) {
			onOpenChange(false);
		}

		sendEvent({
			eventCategory: "Search Bar",
			eventAction: "See all",
			eventLabel:
				typeof trackingContext === "string"
					? trackingContext
					: trackingContext.ga,
		});
	}, [onOpenChange, trackingContext]);

	useEffect(() => {
		const input = ref.current!.querySelector("input");
		if (!input) return;
		if (isOpen) {
			input.focus();
			AnalyticsUtils.sendEvent({
				eventCategory: "Search Bar",
				eventAction: "open",
				eventLabel:
					typeof trackingContext === "string"
						? trackingContext
						: trackingContext.ga,
				mixpanelProperties: {
					name: "Open Search Bar",
					Context: trackingContext,
				},
			});
		} else {
			input.blur();
			AnalyticsUtils.sendEvent({
				eventCategory: "Search Bar",
				eventAction: "close",
				eventLabel:
					typeof trackingContext === "string"
						? trackingContext
						: trackingContext.ga,
				mixpanelProperties: {
					name: "Close Search Bar",
					Context:
						typeof trackingContext === "string"
							? trackingContext
							: trackingContext.ga,
				},
			});
		}
		// eslint-disable-next-line
	}, [isOpen]);

	const searchResultSelected = useCallback(
		(user, e) => {
			if (onOpenChange) {
				onOpenChange(false);
			}
			if (onSelect) {
				onSelect(user, e);
			}

			sendEvent({
				eventCategory: "Search Bar",
				eventAction: "Select result select",
				eventLabel:
					typeof trackingContext === "string"
						? trackingContext
						: trackingContext.ga,
			});

			handleReset();
		},
		[handleReset, onOpenChange, onSelect, trackingContext],
	);

	const handleBlur = useCallback(
		(e: any) => {
			const target = Utils.getRelatedTarget(e);
			if (
				!target ||
				(!ref.current!.contains(target) && !ref.current!.contains(e.target)) ||
				target.nodeName === "BODY"
			) {
				if (onOpenChange) onOpenChange(false);
				handleReset();
			}
		},
		[handleReset, onOpenChange],
	);

	const setCurrentSelection = useCallback((selection: number) => {
		let newSelection = selection;
		if (newSelection < 0) newSelection = 0;
		if (newSelection >= MAX_RESULTS) newSelection = MAX_RESULTS;
		setSelection(newSelection);
	}, []);

	const handleKeyDown = useCallback(
		(e: any) => {
			if (e.key === "Escape") {
				if (onOpenChange) onOpenChange(false);
				handleReset();
			} else if (
				e.key === "ArrowDown" ||
				e.key === "ArrowRight" ||
				e.key === "Tab"
			) {
				e.preventDefault();
				e.stopPropagation();
				setCurrentSelection(selection + 1);
			} else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
				e.preventDefault();
				e.stopPropagation();
				setCurrentSelection(selection - 1);
			} else if (e.key === "Enter") {
				e.preventDefault();
				e.stopPropagation();

				if (onEnterPress) {
					return onEnterPress();
				}

				const el: any = ref.current!.querySelectorAll(
					".SearchBarResultsList__item",
				)[selection];

				if (el) {
					el.click();
				}
			}

			if (
				[
					"Escape",
					"ArrowDown",
					"ArrowRight",
					"Tab",
					"ArrowUp",
					"ArrowLeft",
					"Enter",
				].includes(e.key)
			) {
				sendEvent({
					eventCategory: "Search Bar",
					eventAction: `Key down - ${e.key}`,
					eventLabel:
						typeof trackingContext === "string"
							? trackingContext
							: trackingContext.ga,
				});
			}
		},
		[
			handleReset,
			onEnterPress,
			onOpenChange,
			selection,
			setCurrentSelection,
			trackingContext,
		],
	);

	const handleTooltipOpenChange = useCallback(
		(open: boolean) => {
			if (open && onOpenChange) {
				onOpenChange(open);
			}
		},
		[onOpenChange],
	);

	/*if (onFilterItem) {
      users = users.filter(onFilterItem);
    }*/

	const results = autocomplete?.resolved && autocomplete.value!.length > 0 && (
		<SearchBarResults
			onGetResultLink={onGetResultLink}
			maxResults={MAX_RESULTS}
			selection={selection}
			results={autocomplete?.value}
			onSelect={searchResultSelected}
		/>
	);

	const _noResultsFound = autocomplete && autocomplete.value?.length === 0 && (
		<SearchBarNoResults />
	);

	const _fetchingDrivers = autocomplete && !autocomplete.resolved && (
		<SearchBarFetching />
	);

	const _seeAllDrivers = seeAll && (
		<Link
			className={"SearchBar__see-all"}
			href={seeAll.href}
			onClick={handleSeeAllClick}
		>
			{((seeAll.text as any) || strings.MISC_SEE_ALL).toUpperCase()}
		</Link>
	);

	const _resultsContent = (
		<div className={"SearchBar__results"}>
			{_fetchingDrivers || _noResultsFound || results}
			{_seeAllDrivers}
		</div>
	);

	const _inputBar = (
		<SearchBarInput
			value={input}
			placeholder={placeholder}
			focusPlaceholder={focusPlaceholder}
			searchIsActive={isOpen}
			onChange={handleSearchInput}
			onOpenChange={onOpenChange}
			hideIcon={hideIcon}
		/>
	);

	return (
		<AnalyticsUtils.TrackingContext.Provider
			value={`${trackingContext} Search Bar`}
		>
			<div
				role={"none"}
				ref={ref}
				className={clsx("SearchBar", className, {
					"--visible": isOpen,
				})}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
			>
				<Tooltip
					fillWidth={true}
					showArrow={false}
					disabled={!autocomplete}
					placement={"bottom"}
					onVisibleChange={handleTooltipOpenChange}
					visible={isOpen && input.length >= 1}
					content={_resultsContent}
				>
					{_inputBar}
				</Tooltip>
			</div>
		</AnalyticsUtils.TrackingContext.Provider>
	);
}

export default SearchBar;
